import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, UserPlus, UserMinus, Search, Star, Grid3X3, List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const MyNetwork = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('following');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    if (user && token) fetchAll();
  }, [user, token]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const uid = user?.id || user?.sub;
      const [fwRes, frRes, sgRes] = await Promise.all([
        axios.get(`${API}/social/following/${uid}`),
        axios.get(`${API}/social/followers/${uid}`),
        axios.get(`${API}/social/discover/profiles?limit=20`),
      ]);
      setFollowing(fwRes.data);
      setFollowers(frRes.data);
      // Filter out people already followed + self
      const followingIds = new Set(fwRes.data.map(f => f.id));
      followingIds.add(uid);
      setSuggested((sgRes.data.profiles || []).filter(p => !followingIds.has(p.id)));
    } catch { }
    setLoading(false);
  };

  const handleFollow = async (userId) => {
    try {
      await axios.post(`${API}/social/follow/${userId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Followed!');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      await axios.delete(`${API}/social/follow/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Unfollowed');
      fetchAll();
    } catch {
      toast.error('Failed');
    }
  };

  const filterList = (list) => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(p => p.name?.toLowerCase().includes(q) || p.role?.toLowerCase().includes(q));
  };

  if (!user) {
    return (
      <div className="text-center py-16">
        <Users size={48} className="mx-auto mb-4 text-gray-600" />
        <h2 className="text-xl font-bold mb-2">My Network</h2>
        <p className="text-muted-foreground mb-4">Sign in to see your connections</p>
        <Link to="/login"><Button>Sign In</Button></Link>
      </div>
    );
  }

  const tabs = [
    { key: 'following', label: 'Following', count: following.length },
    { key: 'followers', label: 'Followers', count: followers.length },
    { key: 'discover', label: 'Discover', count: suggested.length },
  ];

  const activeList = activeTab === 'following' ? following : activeTab === 'followers' ? followers : suggested;
  const filtered = filterList(activeList);
  const followingIds = new Set(following.map(f => f.id));

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="my-network-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">My Network</h1>
            <p className="text-sm text-muted-foreground">{following.length} following, {followers.length} followers</p>
          </div>
          <div className="flex gap-1">
            <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className="px-2">
              <Grid3X3 size={14} />
            </Button>
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className="px-2">
              <List size={14} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === tab.key ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-gray-300 bg-card border border-border'}`}
              data-testid={`network-tab-${tab.key}`}
            >
              {tab.label}
              <span className="ml-1.5 text-xs opacity-60">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or role..."
            className="pl-9 bg-card border-border"
            data-testid="network-search"
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-xl border border-border bg-card/30">
            <Users size={40} className="mx-auto mb-3 text-gray-600" />
            <p className="text-gray-500">
              {activeTab === 'following' ? 'You\'re not following anyone yet' :
               activeTab === 'followers' ? 'No followers yet' :
               'No suggestions right now'}
            </p>
            {activeTab !== 'discover' && (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setActiveTab('discover')}>
                Discover People
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((person, i) => (
              <PersonCard
                key={person.id}
                person={person}
                isFollowing={followingIds.has(person.id)}
                isSelf={person.id === (user?.id || user?.sub)}
                onFollow={() => handleFollow(person.id)}
                onUnfollow={() => handleUnfollow(person.id)}
                index={i}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((person, i) => (
              <PersonRow
                key={person.id}
                person={person}
                isFollowing={followingIds.has(person.id)}
                isSelf={person.id === (user?.id || user?.sub)}
                onFollow={() => handleFollow(person.id)}
                onUnfollow={() => handleUnfollow(person.id)}
                index={i}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

const PersonCard = ({ person, isFollowing, isSelf, onFollow, onUnfollow, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: Math.min(index * 0.03, 0.3) }}
    className="rounded-xl border border-border bg-card overflow-hidden group"
    data-testid={`person-card-${person.id}`}
  >
    <Link to={`/u/${person.id}`} className="block">
      <div className="aspect-square relative bg-gradient-to-br from-purple-900/20 to-card">
        {person.profile_pic ? (
          <img src={person.profile_pic} alt={person.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-gray-600">
            {person.name?.charAt(0)}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
      </div>
    </Link>
    <div className="p-3">
      <Link to={`/u/${person.id}`}>
        <p className="font-semibold text-sm truncate hover:text-purple-400 transition">{person.name}</p>
      </Link>
      <p className="text-[10px] text-gray-500 capitalize mb-2">{person.role}</p>
      {person.badges && person.badges.length > 0 && (
        <div className="flex gap-1 mb-2 flex-wrap">
          {person.badges.slice(0, 2).map(b => (
            <span key={b.key} className="text-[8px] px-1 py-px rounded-full font-bold" style={{ background: `${b.color}20`, color: b.color }}>{b.label}</span>
          ))}
        </div>
      )}
      {!isSelf && (
        <Button
          size="sm"
          variant={isFollowing ? "outline" : "default"}
          className="w-full h-7 text-xs"
          onClick={isFollowing ? onUnfollow : onFollow}
          data-testid={`${isFollowing ? 'unfollow' : 'follow'}-${person.id}`}
        >
          {isFollowing ? <><UserMinus size={12} className="mr-1" /> Unfollow</> : <><UserPlus size={12} className="mr-1" /> Follow</>}
        </Button>
      )}
    </div>
  </motion.div>
);

const PersonRow = ({ person, isFollowing, isSelf, onFollow, onUnfollow, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: Math.min(index * 0.02, 0.2) }}
    className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card hover:bg-white/5 transition"
    data-testid={`person-row-${person.id}`}
  >
    <Link to={`/u/${person.id}`}>
      <Avatar className="w-12 h-12">
        <AvatarImage src={person.profile_pic} />
        <AvatarFallback className="bg-purple-500/20 text-purple-400">{person.name?.charAt(0)}</AvatarFallback>
      </Avatar>
    </Link>
    <div className="flex-1 min-w-0">
      <Link to={`/u/${person.id}`}>
        <p className="font-semibold text-sm truncate hover:text-purple-400 transition">{person.name}</p>
      </Link>
      <div className="flex items-center gap-2">
        <p className="text-xs text-gray-500 capitalize">{person.role}</p>
        {person.badges && person.badges.slice(0, 2).map(b => (
          <span key={b.key} className="text-[8px] px-1 py-px rounded-full font-bold" style={{ background: `${b.color}20`, color: b.color }}>{b.label}</span>
        ))}
      </div>
      {person.bio && <p className="text-xs text-gray-500 truncate mt-0.5">{person.bio}</p>}
    </div>
    {person.followers_count !== undefined && (
      <span className="text-xs text-gray-500 shrink-0">{person.followers_count} followers</span>
    )}
    {!isSelf && (
      <Button
        size="sm"
        variant={isFollowing ? "outline" : "default"}
        className="shrink-0 h-8 text-xs"
        onClick={isFollowing ? onUnfollow : onFollow}
      >
        {isFollowing ? 'Unfollow' : 'Follow'}
      </Button>
    )}
  </motion.div>
);

export default MyNetwork;
